import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";

export const useDriverLocation = (driverId = null) => {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    let subscription;

    const fetchInitialLocation = async () => {
      try {
        setLoading(true);
        setError(null);

        // Build query
        let query = supabase
          .from("drivergps")
          .select("*")
          .order("location_timestamp", { ascending: false });

        // If driverId is provided, filter by it
        if (driverId) {
          // Convert driver-001 to driver_001 format
          const formattedDriverId = driverId.replace("-", "_");
          query = query.eq("driver_id", formattedDriverId);
        }

        // Get the most recent location
        const { data, error } = await query.limit(1);

        if (error) {
          console.error("Error fetching driver location:", error);
          setError(error.message);
          return;
        }

        console.log("Fetched GPS data:", data);

        if (data && data.length > 0) {
          const locationData = data[0];
          console.log("Processing location data:", locationData);
          setLocation({
            lat: parseFloat(locationData.latitude),
            lng: parseFloat(locationData.longitude),
            accuracy: locationData.accuracy,
            speed: locationData.speed,
            heading: locationData.heading,
          });
          setLastUpdate(new Date(locationData.location_timestamp));
        } else {
          console.log("No GPS data found in database");
        }
      } catch (err) {
        console.error("Error fetching location:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const setupRealtimeSubscription = () => {
      // Build filter for realtime subscription
      let filter = "*";
      if (driverId) {
        // Convert driver-001 to driver_001 format
        const formattedDriverId = driverId.replace("-", "_");
        filter = `driver_id=eq.${formattedDriverId}`;
      }

      console.log("Setting up real-time subscription with filter:", filter);

      subscription = supabase
        .channel("driver-location-updates")
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "drivergps",
            filter: filter,
          },
          (payload) => {
            console.log("🚗 New location INSERT:", payload);
            const locationData = payload.new;
            setLocation({
              lat: parseFloat(locationData.latitude),
              lng: parseFloat(locationData.longitude),
              accuracy: locationData.accuracy,
              speed: locationData.speed,
              heading: locationData.heading,
            });
            setLastUpdate(new Date(locationData.location_timestamp));
          }
        )
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "drivergps",
            filter: filter,
          },
          (payload) => {
            console.log("🚗 Location UPDATE:", payload);
            const locationData = payload.new;
            setLocation({
              lat: parseFloat(locationData.latitude),
              lng: parseFloat(locationData.longitude),
              accuracy: locationData.accuracy,
              speed: locationData.speed,
              heading: locationData.heading,
            });
            setLastUpdate(new Date(locationData.location_timestamp));
          }
        )
        .subscribe();

      return subscription;
    };

    // Fetch initial location and setup realtime subscription
    fetchInitialLocation();
    setupRealtimeSubscription();

    // Cleanup function
    return () => {
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, [driverId]);

  return {
    location,
    loading,
    error,
    lastUpdate,
  };
};
